import { forwardRef, useState, useEffect, useRef, useCallback } from "react";
import {
  NumberInput as MantineNumberInput,
  NumberInputProps as MantineNumberInputProps,
} from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import classes from "@/styles/TextInput.module.css";
import { ActionIcon } from "./ActionIcon";
import { useStyles } from "@/styles/useStyles";

type MantineNumberInputValue = number | "";

export interface NumberInputProps
  extends Omit<MantineNumberInputProps, "value" | "title"> {
  id: string;
  value: MantineNumberInputValue;
  defaultValue?: MantineNumberInputValue;
  onDebouncedChange?: (value: MantineNumberInputValue) => void;
  debounceDelay?: number;
  onClear?: () => void;
  title: string;
  styles?: any;
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  ({
    id,
    onDebouncedChange,
    debounceDelay = 300,
    onClear,
    value,
    title,
    styles,
    ...props
  }, _ref) => {
    const { theme } = useStyles();
    const [localValue, setLocalValue] = useState<MantineNumberInputValue>(
      value ?? props.defaultValue ?? "",
    );

    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
      setLocalValue(value ?? "");
    }, [value]);

    const handleChange = useCallback(
      (newValue: string | number) => {
        const processedValue: MantineNumberInputValue =
          newValue === "" ? "" : Number(newValue);
        setLocalValue(processedValue);
        props.onChange?.(processedValue);

        // Clear existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        // Set new timeout
        timeoutRef.current = setTimeout(() => {
          onDebouncedChange?.(processedValue);
        }, debounceDelay);
      },
      [props, debounceDelay, onDebouncedChange],
    );

    const handleClear = useCallback(() => {
      setLocalValue("");
      onClear?.();

      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }, [onClear]);

    const hasValue = value !== undefined && value !== "";

    return (
      <MantineNumberInput
        id={id}
        name={id}
        value={localValue}
        title={title}
        aria-label={title}
        aria-invalid={props.error ? true : undefined}
        aria-errormessage={props.error ? `${id}-error` : undefined}
        onChange={handleChange}
        classNames={{ input: classes.textInput }}
        rightSection={
          hasValue && onClear ? (
            <ActionIcon
              ariaLabel="Clear Number Input"
              onClick={handleClear}
              variant="transparent"
              color={!hasValue ? "text.4" : "text.6"}
            >
              <IconX />
            </ActionIcon>
          ) : null
        }
        styles={{
          label: {
            color: theme.colors.text[9],
            fontWeight: 700,
          },
          ...styles,
        }}
        {...props}
      />
    );
  },
);

NumberInput.displayName = "NumberInput";
